import React from 'react';

import { Chip } from './Chip';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import type { EventTypeRef } from '../types/api';

interface EventTypeChipsProps {
  /** Resolved by the API (`eventTypes` / admin `supportedTags`); never rebuilt from a partial list. */
  eventTypes: readonly EventTypeRef[];
  /** Rendered when the list is empty — a dash, or a warning where none is a defect. */
  empty?: React.ReactNode;
  className?: string;
}

/**
 * The event types a template version declares, by name (DESIGN_SYSTEM.md §4
 * "Names, not keys"). One component for the publisher's workbench and the
 * administrator's review, so both sides of one decision read the same thing.
 *
 * Names render in sans: mono is for machine values. An id whose row is gone
 * reads 'Deleted event type' plus a short mono id, with the full id in a
 * focusable tooltip — never a bare UUID, never a `title=` alone.
 */
export const EventTypeChips: React.FC<EventTypeChipsProps> = ({ eventTypes, empty = null, className }) => {
  if (eventTypes.length === 0) return <>{empty}</>;
  return (
    <ul className={['flex flex-wrap gap-1', className].filter(Boolean).join(' ')} aria-label="Event types">
      {eventTypes.map((eventType) => (
        <li key={eventType.id} className="max-w-full">
          {eventType.name ? (
            <Chip>{eventType.name}</Chip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex max-w-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Chip>
                    Deleted event type <span className="font-mono">{eventType.id.slice(0, 8)}</span>
                  </Chip>
                </span>
              </TooltipTrigger>
              <TooltipContent>Event type ID {eventType.id}</TooltipContent>
            </Tooltip>
          )}
        </li>
      ))}
    </ul>
  );
};
